import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { painSlides } from '../data/content';
const toneLabel: Record<string, string> = {
  dolor: 'El problema',
  cambio: 'El cambio',
  resultado: 'El resultado'
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function PainCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = useCallback((next: number) => {
    setIndex((next + painSlides.length) % painSlides.length);
  }, []);
  useEffect(() => {
    if (paused) return;
    const id = window.setTimeout(() => go(index + 1), 6500);
    return () => window.clearTimeout(id);
  }, [index, paused, go]);
  const slide = painSlides[index];
  return <section id="dolores" aria-label="Las etapas de tu relación con el dinero" className="w-full bg-forest-800 py-16 lg:py-24" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-114" data-mp="ys5iof3aawer">
      <div className="mx-auto max-w-6xl px-5 lg:px-8" data-id="element-115" data-mp="1stg8i0golxr4">
        <div className="max-w-2xl" data-id="element-116" data-mp="1xv5n1q344tjy">
          <p className="font-display text-sm font-bold text-gold-500" data-id="element-117" data-mp="1xs9llrs23mqr">
            Antes y después
          </p>
          <h2 className="display-tight mt-4 font-display text-4xl font-extrabold text-cream-50 sm:text-5xl" data-id="element-118" data-mp="26mo0vucl6x9v">
            Hoy el mes te maneja a vos.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-cream-200" data-id="element-119" data-mp="2bl913tru9gyb">
            Estas son las tres etapas por las que pasa cualquiera que aplica el
            sistema. Mirá en cuál estás parado ahora mismo.
          </p>
        </div>

        <div className="mt-12 grid items-center gap-8 lg:grid-cols-[1fr_1fr] lg:gap-14" data-id="element-120" data-mp="2pljsqz0z2zkj">
          <div className="relative overflow-hidden rounded-2xl bg-forest-900" data-id="element-121" data-mp="2pj7fgz65tddt">
            <AnimatePresence mode="wait" initial={false} data-id="element-122" data-mp="r7qogabbsmjb">
              <motion.img key={slide.image} src={slide.image} alt={slide.alt} initial={{
              opacity: 0,
              scale: 1.03
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.28,
              ease: EASE
            }} className="aspect-square w-full object-cover" data-id="element-123" data-mp="v6r3riulh71k" />
            </AnimatePresence>
            <span className="absolute left-4 top-4 rounded-md bg-forest-900/85 px-3 py-1.5 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-gold-400" data-id="element-124" data-mp="23g81rkmtsoqq">
              {slide.stage} · {toneLabel[slide.tone]}
            </span>
          </div>

          <div data-id="element-125" data-mp="2r7dhlzsrnh11">
            <AnimatePresence mode="wait" initial={false} data-id="element-126" data-mp="2r5slw6upts1l">
              <motion.div key={slide.title} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} data-id="element-127" data-mp="2m898181xjha6">
                <h3 className="font-display text-2xl font-extrabold leading-snug text-cream-50 sm:text-3xl" data-id="element-128" data-mp="2v6ulwpwgi84m">
                  {slide.title}
                </h3>
                <p className="mt-5 text-lg leading-relaxed text-cream-200" data-id="element-129" data-mp="3js958q260ftw">
                  {slide.copy}
                </p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-9 flex items-center gap-4" data-id="element-130" data-mp="39umixiijdlzw">
              <button type="button" onClick={() => go(index - 1)} aria-label="Etapa anterior" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-cream-100 transition-colors duration-150 ease-out hover:border-gold-500 hover:text-gold-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-400" data-id="element-131" data-mp="39x8q3h1ilzhl">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-132" data-mp="2d5x3rzyan7hm" />
              </button>
              <button type="button" onClick={() => go(index + 1)} aria-label="Etapa siguiente" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-cream-100 transition-colors duration-150 ease-out hover:border-gold-500 hover:text-gold-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-400" data-id="element-133" data-mp="2i3orsivti9gj">
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-134" data-mp="1re2guffb9776" />
              </button>

              <div className="ml-2 flex items-center gap-2" data-id="element-135" data-mp="1mcwmye9wzdw2">
                {painSlides.map((item, i) => <button key={item.stage} type="button" onClick={() => go(i)} aria-label={`Ver ${item.stage}`} aria-current={i === index} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-400 ${i === index ? 'w-10 bg-gold-500' : 'w-4 bg-white/25 hover:bg-white/40'}`} data-id="element-136" data-mp="1hfmo680mxpvk" data-mpi={i} />)}
              </div>
            </div>
          </div>
        </div>

        <p className="mt-12 max-w-3xl border-l-2 border-gold-500 pl-5 text-lg leading-relaxed text-cream-100" data-id="element-137" data-mp="17d0wr2xmyj0l">
          El problema no es cuánto ganás. Es que tu dinero no tiene un destino
          asignado antes de entrar a tu cuenta. Eso es exactamente lo que
          resuelve el Método de los 4 Bolsillos.
        </p>
      </div>
    </section>;
}